import { ApiResponse } from 'apisauce';

import { ErrorInfo } from '@/shared/interfaces/error-info.interface';
import { ResponseModel } from '@/shared/interfaces/response-model.interface';
import { userApi } from '@/shared/services/apis/user-api.service';
import { userImageQueryKeys } from '@/shared/services/data/user-data/user-image-query-keys';
import { cachedRequest } from '@/shared/services/helpers/cached-request/cached-request.helper';
import errorHelper from '@/shared/services/helpers/error-helper/error-helper.service';

/**
 * Avatar URLs are short-lived pre-signed links, so they are cached only long enough to
 * collapse the burst of identical requests a table of avatars fires on mount — every
 * row used to issue its own call.
 */
const USER_IMAGE_CACHE_CONFIG = {
  staleTime: 60 * 1000, // 1 minute
  gcTime: 5 * 60 * 1000 // 5 minutes
};

/**
 * Fetches the current user's image URL with proper error handling
 * @param containerName - Name of the container/component requesting the image
 * @returns Promise resolving to image URL string or ErrorInfo
 */
export async function getCurrentUserImage(containerName: string): Promise<string | ErrorInfo> {
  return cachedRequest(
    userImageQueryKeys.currentUser(),
    () => fetchCurrentUserImage(containerName),
    USER_IMAGE_CACHE_CONFIG
  );
}

async function fetchCurrentUserImage(containerName: string): Promise<string | ErrorInfo> {
  return userApi
    .getUserImage()
    .then((response: ApiResponse<ResponseModel<{ url: string }>, ErrorInfo>) => {
      if (response.ok) {
        const responseData = response.data;
        if (responseData && responseData.data && responseData.data.url) {
          return responseData.data.url;
        } else {
          const error: ErrorInfo = errorHelper(
            0,
            'No image URL received',
            containerName,
            'getCurrentUserImage'
          );
          return error;
        }
      } else {
        const error: ErrorInfo = errorHelper(
          Number(response.status),
          response.data && response.data.errorCode
            ? String(response.data.errorCode || '')
            : response.problem,
          containerName,
          'getCurrentUserImage'
        );
        return error;
      }
    })
    .catch((error) => {
      const errorInfo: ErrorInfo = errorHelper(
        0,
        error.message || 'Failed to fetch user image',
        containerName,
        'getCurrentUserImage'
      );
      return errorInfo;
    });
}

/**
 * Fetches a specific user's image URL by ID with proper error handling
 * @param userId - The ID of the user whose image to fetch
 * @param containerName - Name of the container/component requesting the image
 * @returns Promise resolving to image URL string or ErrorInfo
 */
export async function getUserImageById(
  userId: string,
  containerName: string
): Promise<string | ErrorInfo> {
  return cachedRequest(
    userImageQueryKeys.byUserId(userId),
    () => fetchUserImageById(userId, containerName),
    USER_IMAGE_CACHE_CONFIG
  );
}

async function fetchUserImageById(
  userId: string,
  containerName: string
): Promise<string | ErrorInfo> {
  return userApi
    .getUserImage(userId)
    .then((response: ApiResponse<ResponseModel<{ url: string }>, ErrorInfo>) => {
      if (response.ok) {
        const responseData = response.data;
        if (responseData && responseData.data && responseData.data.url) {
          return responseData.data.url;
        } else {
          const error: ErrorInfo = errorHelper(
            0,
            'No image URL received',
            containerName,
            'getUserImageById'
          );
          return error;
        }
      } else {
        const error: ErrorInfo = errorHelper(
          Number(response.status),
          response.data && response.data.errorCode
            ? String(response.data.errorCode || '')
            : response.problem,
          containerName,
          'getUserImageById'
        );
        return error;
      }
    })
    .catch((error) => {
      const errorInfo: ErrorInfo = errorHelper(
        0,
        error.message || 'Failed to fetch user image',
        containerName,
        'getUserImageById'
      );
      return errorInfo;
    });
}
